<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import AgenteIaAPI from 'dashboard/api/agenteIa';
import Button from 'dashboard/components-next/button/Button.vue';
import AgenteIaLayout from '../components/AgenteIaLayout.vue';
import SomenteLeituraOperadoMotor from '../components/SomenteLeituraOperadoMotor.vue';
import { PROVEDORES, modelosDoProvedor } from '../chaves';

const { t } = useI18n();
const layout = ref(null);
const carregando = ref(true);
const salvando = ref(false);
const dados = ref({ payload: [], modelo: {}, modelos: [] });
const provedor = ref('openrouter');
const valor = ref('');

const falhou = e =>
  useAlert(e?.response?.data?.error || t('AGENTE_IA.CHAVES.ERRO'));

const carregar = async () => {
  try {
    dados.value = (await AgenteIaAPI.chaves()).data;
  } catch (e) {
    falhou(e);
  } finally {
    carregando.value = false;
  }
};

onMounted(carregar);

const escolhido = computed(() => PROVEDORES.find(p => p.id === provedor.value));
const nomeDoProvedor = id =>
  t(`AGENTE_IA.CHAVES.PROVEDORES.${id.toUpperCase()}.NOME`);
const modelosEmUso = computed(() =>
  modelosDoProvedor(dados.value.modelos, dados.value.modelo?.provider)
);

const salvar = async () => {
  salvando.value = true;
  try {
    const { data } = await AgenteIaAPI.criarChave({
      kind: escolhido.value.kind,
      name: nomeDoProvedor(provedor.value),
      value: valor.value.trim(),
    });
    dados.value = data;
    valor.value = '';
    layout.value?.carregarStatus();
    useAlert(t('AGENTE_IA.CHAVES.SALVA'));
  } catch (e) {
    falhou(e);
  } finally {
    salvando.value = false;
  }
};

const avisoDoCopiloto = computed(() => {
  if (!dados.value.copiloto_nao_acompanhou) return '';
  const { copiloto_provedor: atual } = dados.value;
  return atual
    ? t('AGENTE_IA.CHAVES.COPILOTO_NAO_ACOMPANHOU', {
        provedor: nomeDoProvedor(atual),
      })
    : t('AGENTE_IA.CHAVES.COPILOTO_SEM_CHAVE');
});

const usar = async (chave, model) => {
  try {
    dados.value = (await AgenteIaAPI.usarChave(chave.id, model)).data;
    layout.value?.carregarStatus();
    useAlert(t('AGENTE_IA.SALVO'));
  } catch (e) {
    falhou(e);
  }
};

const testar = async chave => {
  try {
    const { data } = await AgenteIaAPI.testarChave(chave.id);
    useAlert(
      data.ok === false
        ? t('AGENTE_IA.CHAVES.TESTE_FALHOU')
        : t('AGENTE_IA.CHAVES.TESTE_OK')
    );
  } catch (e) {
    falhou(e);
  }
};

const apagar = async chave => {
  try {
    await AgenteIaAPI.apagarChave(chave.id);
    await carregar();
  } catch (e) {
    falhou(e);
  }
};

const trocarModelo = modelo => {
  const emUso = dados.value.payload.find(chave => chave.em_uso);
  if (emUso && modelo !== dados.value.modelo.model) usar(emUso, modelo);
};
</script>

<template>
  <AgenteIaLayout
    ref="layout"
    :title="t('AGENTE_IA.CHAVES.TITULO')"
    :description="t('AGENTE_IA.CHAVES.DESCRICAO')"
  >
    <SomenteLeituraOperadoMotor>
      <p v-if="carregando" class="text-sm text-n-slate-11">
        {{ t('AGENTE_IA.CARREGANDO') }}
      </p>
      <template v-else>
        <p
          v-if="avisoDoCopiloto"
          class="p-3 text-sm rounded-xl bg-n-amber-3 text-n-amber-11"
        >
          {{ avisoDoCopiloto }}
        </p>
        <section v-if="dados.payload.length" class="flex flex-col gap-3">
          <h2 class="text-sm font-medium text-n-slate-12">
            {{ t('AGENTE_IA.CHAVES.SUAS') }}
          </h2>
          <div
            v-for="chave in dados.payload"
            :key="chave.id"
            class="flex flex-wrap items-center justify-between gap-3 p-4 rounded-xl outline outline-1"
            :class="
              chave.em_uso ? 'outline-n-blue-7 bg-n-blue-2' : 'outline-n-weak'
            "
          >
            <div class="flex flex-col gap-0.5">
              <span class="text-sm font-medium text-n-slate-12">{{
                chave.name
              }}</span>
              <span
                v-if="chave.em_uso"
                class="text-xs font-medium text-n-blue-11"
              >
                {{ t('AGENTE_IA.CHAVES.EM_USO') }}
              </span>
            </div>
            <div class="flex flex-wrap items-center gap-2">
              <Button
                v-if="!chave.em_uso"
                size="sm"
                :label="t('AGENTE_IA.CHAVES.USAR')"
                @click="usar(chave)"
              />
              <Button
                size="sm"
                variant="outline"
                :label="t('AGENTE_IA.CHAVES.TESTAR')"
                @click="testar(chave)"
              />
              <Button
                size="sm"
                variant="ghost"
                color="ruby"
                :label="t('AGENTE_IA.CHAVES.APAGAR')"
                @click="apagar(chave)"
              />
            </div>
          </div>
        </section>

        <section
          v-if="
            dados.payload.some(chave => chave.em_uso) && modelosEmUso.length
          "
          class="flex flex-col gap-3"
        >
          <div class="flex flex-col gap-1">
            <h2 class="text-sm font-medium text-n-slate-12">
              {{ t('AGENTE_IA.CHAVES.MODELO.TITULO') }}
            </h2>
            <p class="text-xs text-n-slate-11">
              {{ t('AGENTE_IA.CHAVES.MODELO.AJUDA') }}
            </p>
          </div>
          <div class="grid gap-2 sm:grid-cols-2">
            <button
              v-for="modelo in modelosEmUso"
              :key="modelo.id"
              type="button"
              class="flex flex-col gap-1 p-3 text-left rounded-xl outline outline-1"
              :class="
                modelo.model === dados.modelo.model
                  ? 'outline-n-blue-7 bg-n-blue-2'
                  : 'outline-n-weak hover:bg-n-alpha-1'
              "
              @click="trocarModelo(modelo.model)"
            >
              <span class="flex flex-wrap items-center gap-2">
                <span class="text-sm font-medium text-n-slate-12">
                  {{ modelo.model }}
                </span>
                <span
                  class="px-1.5 py-0.5 text-xs rounded-md"
                  :class="{
                    'bg-n-teal-3 text-n-teal-11': modelo.faixa === 'economico',
                    'bg-n-blue-3 text-n-blue-11':
                      modelo.faixa === 'equilibrado',
                    'bg-n-amber-3 text-n-amber-11': modelo.faixa === 'premium',
                  }"
                >
                  {{
                    t(
                      `AGENTE_IA.CHAVES.MODELO.FAIXA.${modelo.faixa.toUpperCase()}`
                    )
                  }}
                </span>
                <span
                  v-if="modelo.recomendado"
                  class="px-1.5 py-0.5 text-xs rounded-md bg-n-alpha-2 text-n-slate-12"
                >
                  {{ t('AGENTE_IA.CHAVES.MODELO.RECOMENDADO') }}
                </span>
              </span>
              <span class="text-xs text-n-slate-11">
                {{
                  t('AGENTE_IA.CHAVES.MODELO.CUSTO', {
                    valor: modelo.custo_mil,
                  })
                }}
              </span>
            </button>
          </div>
        </section>

        <section class="flex flex-col gap-4">
          <div class="flex flex-col gap-1">
            <h2 class="text-sm font-medium text-n-slate-12">
              {{ t('AGENTE_IA.CHAVES.NOVA') }}
            </h2>
            <p class="text-xs text-n-slate-11">
              {{ t('AGENTE_IA.CHAVES.NOVA_AJUDA') }}
            </p>
          </div>
          <div class="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            <button
              v-for="item in PROVEDORES"
              :key="item.id"
              type="button"
              class="flex flex-col gap-1 p-3 text-left rounded-xl outline outline-1"
              :class="
                provedor === item.id
                  ? 'outline-n-blue-7 bg-n-blue-2'
                  : 'outline-n-weak hover:bg-n-alpha-1'
              "
              @click="provedor = item.id"
            >
              <span class="text-sm font-medium text-n-slate-12">{{
                nomeDoProvedor(item.id)
              }}</span>
              <span class="text-xs text-n-slate-11">
                {{
                  t(
                    `AGENTE_IA.CHAVES.PROVEDORES.${item.id.toUpperCase()}.RESUMO`
                  )
                }}
              </span>
            </button>
          </div>

          <ol
            class="flex flex-col gap-1 pl-5 text-sm list-decimal text-n-slate-12"
          >
            <li>
              {{ t('AGENTE_IA.CHAVES.PASSO_1') }}
              <a
                :href="escolhido.link"
                target="_blank"
                rel="noopener noreferrer"
                class="text-n-blue-11 underline"
              >
                {{ escolhido.link }}
              </a>
            </li>
            <li>{{ t('AGENTE_IA.CHAVES.PASSO_2') }}</li>
            <li>{{ t('AGENTE_IA.CHAVES.PASSO_3') }}</li>
          </ol>

          <form
            class="flex flex-col gap-2 sm:flex-row"
            @submit.prevent="salvar"
          >
            <input
              v-model="valor"
              type="password"
              autocomplete="off"
              class="flex-1 px-3 py-2 text-sm border rounded-lg bg-n-alpha-black2 border-n-weak text-n-slate-12"
              :placeholder="
                t('AGENTE_IA.CHAVES.COLE_AQUI', {
                  provedor: nomeDoProvedor(provedor),
                })
              "
            />
            <Button
              type="submit"
              :label="t('AGENTE_IA.CHAVES.SALVAR')"
              :is-loading="salvando"
              :disabled="!valor.trim()"
            />
          </form>
          <p class="text-xs text-n-slate-11">
            {{ t('AGENTE_IA.CHAVES.SEGURANCA') }}
          </p>
        </section>
      </template>
    </SomenteLeituraOperadoMotor>
  </AgenteIaLayout>
</template>
